import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SlidersHorizontal } from 'lucide-react';
import { isApiEnabled } from '../config/env';
import { productService } from '../services/productService';
import ShopFiltersPanel from '../components/commerce/ShopFiltersPanel';
import {
  ShopActiveFilterChips,
  ShopCatalogPagination,
  ShopFilterTrigger,
} from '../components/commerce/ShopPlpChrome';
import {
  buildCatalogPlpApiParams,
  countActivePlpFilters,
  parseCatalogPlpSearchParams,
  patchCatalogPlpSearchParams,
  plpMatchesPriceBucket,
  priceBucketToRange,
  PRICE_BUCKET_DEFS,
  PLP_PAGE_SIZE,
} from '../utils/shopPlpQuery';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchShopCatalog } from '../store/actions/productActions';
import { ProductCard } from '../components/commerce/ProductCard';
import { ProductCardsGrid } from '../components/commerce/ProductCardLayout';
import { Button } from '../components/ui/Button';
import { Container } from '../components/layout/Primitives';
import PageSeo from '../components/seo/PageSeo';

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  font-size: clamp(10px, 0.12vw + 9px, 13px);
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const CrumbLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.pageDisplay};
  font-weight: 300;
`;

const Lead = styled.p`
  margin: 0 0 24px;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.type.body};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px 0;
  margin-bottom: 24px;
`;

const ToolLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToolRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToolMeta = styled.span`
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const SortLabel = styled.span`
  display: none;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: inline;
  }
`;

const SortSelect = styled.select`
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: inherit;
  font-weight: 500;
  border: none;
  background: transparent;
  cursor: pointer;
  outline: none;
`;

const ProductGrid = styled(ProductCardsGrid)`
  padding-bottom: clamp(48px, 8vw, 88px);
`;

const Empty = styled.div`
  text-align: center;
  padding: clamp(48px, 10vw, 88px) 0;
`;

export default function Search() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const plp = useMemo(() => parseCatalogPlpSearchParams(searchParams), [searchParams]);
  const q = plp.keyword || '';

  const apiCatalog = useAppSelector((s) =>
    Array.isArray(s.product?.shopCatalogList) ? s.product.shopCatalogList : [],
  );
  const catalogLoading = useAppSelector((s) => s.product.shopCatalogLoading);
  const catalogError = useAppSelector((s) => s.product.shopCatalogError);
  const pagination = useAppSelector((s) => s.product?.shopCatalogPagination);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ colors: [], sizes: [] });

  const useApi = isApiEnabled();

  const updatePlp = (patch) => {
    setSearchParams(patchCatalogPlpSearchParams(searchParams, patch));
  };

  useEffect(() => {
    if (!useApi || !q) return undefined;
    dispatch(fetchShopCatalog(buildCatalogPlpApiParams(plp, {})));
    return undefined;
  }, [useApi, dispatch, plp, q]);

  useEffect(() => {
    if (!useApi || !q) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const opts = await productService.getFilterOptions({});
        if (!cancelled) {
          setFilterOptions({ colors: opts.colors || [], sizes: opts.sizes || [] });
        }
      } catch {
        if (!cancelled) setFilterOptions({ colors: [], sizes: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useApi, q]);

  useEffect(() => {
    if (plp.page > 1) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [plp.page]);

  const activePriceBucketId = useMemo(() => {
    const match = PRICE_BUCKET_DEFS.find((b) => plpMatchesPriceBucket(plp, b.id));
    return match?.id ?? null;
  }, [plp]);

  const filterChips = useMemo(() => {
    const chips = [];
    plp.colors.forEach((c) => chips.push({ key: `color:${c}`, label: c }));
    plp.sizes.forEach((s) => chips.push({ key: `size:${s}`, label: s }));
    if (activePriceBucketId) {
      const def = PRICE_BUCKET_DEFS.find((b) => b.id === activePriceBucketId);
      if (def) chips.push({ key: 'price', label: t(`modamai.shop.${def.labelKey}`) });
    }
    if (plp.sale) chips.push({ key: 'sale', label: t('modamai.shop.onSale') });
    return chips;
  }, [plp, activePriceBucketId, t]);

  const handleRemoveChip = (key) => {
    if (key.startsWith('color:')) {
      updatePlp({ colors: plp.colors.filter((x) => x !== key.slice(6)) });
    } else if (key.startsWith('size:')) {
      updatePlp({ sizes: plp.sizes.filter((x) => x !== key.slice(5)) });
    } else if (key === 'price') {
      updatePlp({ minPrice: undefined, maxPrice: undefined });
    } else if (key === 'sale') {
      updatePlp({ sale: false });
    }
  };

  const clearFiltersKeepQuery = () => {
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    setSearchParams(next);
  };

  const totalResults = pagination?.total ?? apiCatalog.length;
  const totalPages = pagination?.pages ?? 1;
  const currentPage = pagination?.page ?? plp.page;
  const activeFilterCount = countActivePlpFilters(plp);

  return (
    <>
      <PageSeo
        documentTitle={
          q ? `${t('modamai.search.title', { q })} — Moda MAI` : `${t('modamai.search.titleEmpty')} — Moda MAI`
        }
        description={
          q ? t('modamai.search.description', { q }) : t('modamai.search.descriptionEmpty')
        }
        noindex={!q}
        canonicalPath={q ? `/search?q=${encodeURIComponent(q)}` : '/search'}
      />
      <Container className="animate-fade-in">
        <Breadcrumb aria-label={t('modamai.shop.breadcrumbAria')}>
          <CrumbLink to="/">{t('modamai.shop.home')}</CrumbLink>
          <span aria-hidden>/</span>
          <span>{t('modamai.search.breadcrumb')}</span>
        </Breadcrumb>

        <PageTitle>{t('modamai.search.heading')}</PageTitle>
        {q ? (
          <Lead>{t('modamai.search.resultsFor', { q, count: totalResults })}</Lead>
        ) : (
          <Lead>{t('modamai.search.prompt')}</Lead>
        )}

        {!useApi ? (
          <Empty>{t('modamai.shop.liveCatalogDisabledLead')}</Empty>
        ) : !q ? (
          <Empty>
            <Lead>{t('modamai.search.prompt')}</Lead>
            <Link to="/shop/women" style={{ marginTop: 16, display: 'inline-block' }}>
              {t('modamai.search.browseWomen')}
            </Link>
          </Empty>
        ) : (
          <>
            <ShopActiveFilterChips
              chips={filterChips}
              onRemove={handleRemoveChip}
              clearAllLabel={t('modamai.shop.clearAll')}
              onClearAll={filterChips.length ? clearFiltersKeepQuery : null}
            />
            <Toolbar>
              <ToolLeft>
                <ShopFilterTrigger
                  activeCount={activeFilterCount}
                  onClick={() => setFiltersOpen(true)}
                  label={t('modamai.shop.filters')}
                  icon={SlidersHorizontal}
                />
              </ToolLeft>
              <ToolRight>
                <ToolMeta>{t('modamai.shop.results', { count: totalResults })}</ToolMeta>
                <SortLabel>{t('modamai.shop.sortBy')}</SortLabel>
                <SortSelect value={plp.sort} onChange={(e) => updatePlp({ sort: e.target.value })}>
                  <option value="recommended">{t('modamai.shop.sortRecommended')}</option>
                  <option value="newest">{t('modamai.shop.sortNewest')}</option>
                  <option value="price-low">{t('modamai.shop.sortPriceLow')}</option>
                  <option value="price-high">{t('modamai.shop.sortPriceHigh')}</option>
                </SortSelect>
              </ToolRight>
            </Toolbar>

            {catalogError ? (
              <Empty>
                <Lead>{catalogError}</Lead>
                <Button
                  type="button"
                  $variant="outlineLuxe"
                  onClick={() => dispatch(fetchShopCatalog(buildCatalogPlpApiParams(plp, {})))}
                >
                  {t('modamai.shop.retry')}
                </Button>
              </Empty>
            ) : catalogLoading && apiCatalog.length === 0 ? (
              <Empty aria-busy>{t('modamai.shop.loadingCatalog')}</Empty>
            ) : apiCatalog.length === 0 ? (
              <Empty>
                <Lead>{t('modamai.search.noResults', { q })}</Lead>
                <Button type="button" $variant="outlineLuxe" onClick={clearFiltersKeepQuery}>
                  {t('modamai.shop.clearFilters')}
                </Button>
              </Empty>
            ) : (
              <>
                <ProductGrid>
                  {apiCatalog.map((p) => (
                    <ProductCard key={p.slug || p.id} product={p} />
                  ))}
                </ProductGrid>
                <ShopCatalogPagination
                  page={currentPage}
                  pages={totalPages}
                  total={totalResults}
                  limit={PLP_PAGE_SIZE}
                  disabled={catalogLoading}
                  onPageChange={(p) => updatePlp({ page: p, resetPage: false })}
                  summaryLabel={({ from, to, total: tot }) =>
                    t('modamai.shop.paginationSummary', { from, to, total: tot })
                  }
                  previousLabel={t('modamai.shop.paginationPrev')}
                  nextLabel={t('modamai.shop.paginationNext')}
                />
              </>
            )}
          </>
        )}
      </Container>

      <ShopFiltersPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        titleLabel={t('modamai.shop.filter')}
        closeAria={t('modamai.shop.closeFilters')}
        saleLabel={t('modamai.shop.onSale')}
        saleOn={plp.sale}
        onSaleChange={(v) => updatePlp({ sale: v })}
        showSaleToggle
        priceSectionLabel={t('modamai.shop.priceRange')}
        tPriceLabel={(key) => t(`modamai.shop.${key}`)}
        activePriceBucketId={activePriceBucketId}
        onPriceBucketChange={(id) => {
          if (!id) updatePlp({ minPrice: undefined, maxPrice: undefined });
          else updatePlp(priceBucketToRange(id));
        }}
        colorSectionLabel={t('modamai.shop.color')}
        colorOptions={filterOptions.colors}
        selectedColors={plp.colors}
        onColorsChange={(c) => updatePlp({ colors: c })}
        sizeSectionLabel={t('modamai.shop.size')}
        sizeOptions={filterOptions.sizes}
        selectedSizes={plp.sizes}
        onSizesChange={(s) => updatePlp({ sizes: s })}
        applyLabel={t('modamai.shop.viewPieces', { count: totalResults })}
        clearLabel={t('modamai.shop.clearAll')}
        onClear={clearFiltersKeepQuery}
      />
    </>
  );
}
